import { Component, effect, HostBinding, inject, input, model, output, signal, viewChild } from '@angular/core';
import {
  WorkContainertype,
  WorkContainertypesV2Service,
  WorkEquipment,
  WorkItem,
  WorkMegasearchResultDto,
  WorkSearchCategoryEnum,
  WorkStocklocation,
  WorkUser,
} from '@work-frontend-monorepo/work-rest-api';
import { lastValueFrom } from 'rxjs';

import { MegasearchResultClickedInterface } from '../global-megasearch-result-contact-and-projects/global-megasearch-result-contact-and-projects.component';
import {
  GlobalMegasearchResultsComponent,
  MegaSearchResultAlignment,
} from '../global-megasearch-results/global-megasearch-results.component';
import { TranslateModule } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { ModalComponent } from '@work-frontend-monorepo/work-ui/modals';
import { CheckboxComponent, InputFieldComponent } from '@work-frontend-monorepo/work-ui/forms';
import { AutofocusDirective } from '@work-frontend-monorepo/work-ui/directives';
import { ButtonComponent, IconComponent } from '@work-frontend-monorepo/work-ui/layout';
import { ExceptionHandlingService, MegasearchService } from '@work-frontend-monorepo/work-ui/services';
import { LoadingAndFeedbackState } from '@work-frontend-monorepo/work-ui/utilities';
import { GetFullnameOfContactPipe } from '@work-frontend-monorepo/work-ui/pipes';

@Component({
  selector: 'work-office-global-megasearch-input',
  templateUrl: './global-megasearch-input.component.html',
  styleUrls: ['./global-megasearch-input.component.scss'],
  standalone: true,
  imports: [
    InputFieldComponent,
    GlobalMegasearchResultsComponent,
    ModalComponent,
    TranslateModule,
    AutofocusDirective,
    ButtonComponent,
    IconComponent,
    NgTemplateOutlet,
    CheckboxComponent,
  ],
  providers: [GetFullnameOfContactPipe],
})
export class GlobalMegasearchInputComponent {
  private readonly containertypesV2Service = inject(WorkContainertypesV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly megasearchService = inject(MegasearchService);
  private readonly getFullnameOfContactPipe = inject(GetFullnameOfContactPipe);

  @HostBinding('style.maxWidth') hostMaxWidth = '100%';
  // input options
  public searchTerm = model('');
  public actAsModal = input(false);
  public required = input(false);
  public label = input('');
  public labelSize = input<'small' | 'default'>('small');
  public disabled = input(false);
  public readonly = input(false);
  public readonlyNoBorders = input(false);
  public invalid = input(false);
  public hintMessage = input('HINWEIS');
  public hideHint = input(false);
  public iconName = input('search');
  public editable = input(false);
  public maxWidth = input<number | string | undefined>(undefined);
  public width = input<number | string | undefined>(undefined);
  public placeholder = input('');
  public showClearButton = input(false);
  public tableInput = input(false);
  public disableHint = input(false);
  public disableResultContactLinks = input(false);
  public disableResultProjectLinks = input(false);
  public itemIdsToExclude = input<number[]>([]);
  /** Artikel dieser Warengruppen nicht anbieten (z. B. Montage/Deckungsbeitrag in der Nachkalkulation). */
  public productgroupIdsToExclude = input<number[]>([]);
  // config
  public resetValueAfterSelection = input(false);
  public alignment = input<MegaSearchResultAlignment>('start');
  public searchCategories = input<WorkSearchCategoryEnum[]>([]);
  public onlySoldProjects = input(false);
  public containerTypeIdFilters = model<number[]>([]);
  // outputs
  public projectSelected = output<MegasearchResultClickedInterface>();
  public contactSelected = output<MegasearchResultClickedInterface>();
  public userSelected = output<WorkUser>();
  public equipmentSelected = output<WorkEquipment>();
  public stocklocationSelected = output<WorkStocklocation>();
  public itemSelected = output<WorkItem>();
  public valueCleared = output();
  public modalClosed = output();
  // internal
  protected modal = viewChild<ModalComponent>('megaSearchModal');
  protected WorkMegasearchResultDto = signal<WorkMegasearchResultDto | undefined>(undefined);
  protected isSearchVisible = signal<boolean>(false);
  protected searchLoadingState = signal<LoadingAndFeedbackState>('none');
  protected containerTypes = signal<WorkContainertype[]>([]);

  private resetValue = signal<string>('');
  private newValueSelected = signal(false);

  constructor() {
    effect(() => {
      if (this.maxWidth()) {
        this.hostMaxWidth = this.maxWidth() + 'px';
      }
    });
    effect(() => {
      if (this.containerTypeIdFilters()) {
        this.loadContainerTypes().then();
      }
    });
  }

  public clearValue() {
    this.searchTerm.set('');
    this.WorkMegasearchResultDto.set(undefined);
    this.valueCleared.emit();
    this.megasearchService.cancelExistingSearch();
  }

  public async openModal() {
    this.modal()?.open();
    this.newValueSelected.set(false);
    if (this.searchTerm()) {
      // there is already a selected option when opening, assign value as fallback, in case no new value is selected but the modal is closed
      this.resetValue.set(this.searchTerm());
      await this.searchForValue(this.searchTerm());
    }
  }

  protected async searchForValue(searchterm: string) {
    this.searchTerm.set(searchterm);
    const trimmedTerm = searchterm.trim();
    if (trimmedTerm.length >= 3) {
      this.searchLoadingState.set('loading');
      try {
        const result = await this.megasearchService.doSearch(trimmedTerm, this.searchCategories(), this.onlySoldProjects());
        this.filterResults(result);
        this.WorkMegasearchResultDto.set(result);
        this.searchLoadingState.set('success');
      } catch (searchException) {
        this.searchLoadingState.set('error');
        this.WorkMegasearchResultDto.set(undefined);
      }
    } else {
      this.megasearchService.cancelExistingSearch();
      this.WorkMegasearchResultDto.set(undefined);
      this.searchLoadingState.set('none');
    }
  }

  protected handleContactClicked(clickedInterface: MegasearchResultClickedInterface): void {
    this.contactSelected.emit(clickedInterface);
    this.searchTerm.set(`${this.getFullnameOfContactPipe.transform(clickedInterface.WorkContactAndProjectsResultDto.contact)}`);
    this.newValueSelected.set(true);
    this.modal()?.close();
  }

  protected handleProjectClicked(clickedInterface: MegasearchResultClickedInterface): void {
    this.projectSelected.emit(clickedInterface);
    this.searchTerm.set(clickedInterface.selectedProject?.numberQualified || '');
    this.newValueSelected.set(true);
    this.modal()?.close();
  }

  protected handleUserClicked(user: WorkUser): void {
    this.userSelected.emit(user);
    this.searchTerm.set(`${user.firstName} ${user.lastName}`);
    this.newValueSelected.set(true);
    this.modal()?.close();
  }

  protected handleEquipmentClicked(equipment: WorkEquipment): void {
    this.equipmentSelected.emit(equipment);
    this.searchTerm.set(`${equipment.brand} ${equipment.model} (${equipment.identifier})`);
    this.newValueSelected.set(true);
    this.modal()?.close();
  }

  protected handleStocklocationClicked(stocklocation: WorkStocklocation): void {
    this.stocklocationSelected.emit(stocklocation);
    this.searchTerm.set(stocklocation.name);
    this.newValueSelected.set(true);
    this.modal()?.close();
  }

  protected handleItemClicked(item: WorkItem): void {
    this.itemSelected.emit(item);
    this.searchTerm.set(item.name || '');
    this.newValueSelected.set(true);
    this.modal()?.close();
  }

  protected handleModalClosed() {
    this.modalClosed.emit();
    if (!this.newValueSelected() && this.resetValue()) {
      this.searchTerm.set(this.resetValue());
    } else if (this.resetValueAfterSelection()) {
      this.clearValue();
    }
  }

  protected updateContainerTypeIdFilter(id: number, checked: boolean) {
    this.clearValue();
    this.containerTypeIdFilters.set(
      checked ? [...this.containerTypeIdFilters(), id] : this.containerTypeIdFilters().filter((filter) => filter !== id),
    );
  }

  private async loadContainerTypes() {
    try {
      this.containerTypes.set(await lastValueFrom(this.containertypesV2Service.containertypesV2ControllerFindAll()));
    } catch (loadContainertypesException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadContainertypesException', loadContainertypesException);
    }
  }

  private filterResults(allResults: WorkMegasearchResultDto): void {
    if (this.itemIdsToExclude().length && allResults.items.length) {
      allResults.items = allResults.items.filter((item) => this.itemIdsToExclude().indexOf(item.entity.id) === -1);
    }
    if (this.productgroupIdsToExclude().length && allResults.items.length) {
      allResults.items = allResults.items.filter((item) => !this.productgroupIdsToExclude().includes(item.entity.productgroupId ?? 0));
    }
  }
}
